import { createContext, useContext, useEffect, useState } from 'react';
import { setTheme as applyXdTheme, tokens, type XdTheme } from '@xdface/tokens';

export type Theme = XdTheme;

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  setTheme: () => {},
  toggle: () => {},
});

/** Existing vault key. The attribute rename keeps this so a saved choice survives. */
export const THEME_STORAGE_KEY = 'vault-theme';

const THEME_ORDER: Theme[] = ['light-mono', 'light', 'sepia', 'dark'];

function readStoredTheme(): Theme {
  const saved = localStorage.getItem(THEME_STORAGE_KEY);
  if (saved === 'light-mono' || saved === 'light' || saved === 'dark' || saved === 'sepia')
    return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    const root = document.documentElement;
    applyXdTheme(theme, root);
    root.classList.remove('dark');
    root.removeAttribute('data-theme');
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', tokens.theme[theme].chrome);
  }, [theme]);

  const setTheme = (next: Theme) => setThemeState(next);

  const toggle = () =>
    setThemeState((current) => {
      const index = THEME_ORDER.indexOf(current);
      return THEME_ORDER[(index + 1) % THEME_ORDER.length];
    });

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>{children}</ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
